import { EventEmitter, Injectable, Injector } from '@angular/core';
import { DefaultLangChangeEvent, LangChangeEvent, TranslateService, TranslationChangeEvent } from '@ngx-translate/core';
import { LANGUAGE } from 'shared-components';

import * as EN_CA from '../../../assets/i18n/en-CA.json';
import * as FR_CA from '../../../assets/i18n/fr-CA.json';
import * as ES_MX from '../../../assets/i18n/es-MX.json';
import * as PT_PT from '../../../assets/i18n/pt-PT.json';

import * as ES_MX_TRANSLATE from '../../../assets/translate/es-mx.translate.json';
import * as FR_CA_TRANSLATE from '../../../assets/translate/fr-ca.translate.json';
import * as PT_PT_TRANSLATE from '../../../assets/translate/pt-pt.translate.json';

import { LOCALE_CONFIG } from 'env/locale-config';
import { Observable, fromEventPattern, merge} from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';

import { TranslationLoaderHelper } from 'app/core/internationalization/translation-loader-helper';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  public static readonly SESSION_LANGUAGE_KEY = 'KH.Locale';
  public static readonly DEFAULT_LANGUAGE = LOCALE_CONFIG.APP_LOCALE as LANGUAGE;

  private _translateService!: TranslateService;

  /**
   * Listens to the translate service for all updates on the keys and emits when there is any change
   * This mimics the exact functionality of the (| translate) pipe and allows us to us it in the typescript
   */
  public translationsReady$ = new Observable<TranslateService>;

  public constructor(private readonly injector: Injector) { }

  public initializeTranslations(): void {
    this._translateService = this.injector.get(TranslateService);
    const defaultLang = TranslationService.DEFAULT_LANGUAGE;

    // set older translations for backward compatibility
    this._translateService.setTranslation(LANGUAGE.FRENCH_CANADA, FR_CA_TRANSLATE);
    this._translateService.setTranslation(LANGUAGE.SPANISH_MEXICO, ES_MX_TRANSLATE);
    this._translateService.setTranslation(LANGUAGE.SPANISH_COLOMBIA, ES_MX_TRANSLATE);
    this._translateService.setTranslation(LANGUAGE.PORTUGUESE_PORTUGAL, PT_PT_TRANSLATE);

    // set/merge new common app translations
    // we may want to load the other non default languages dynamically in the future if the app grows
    // however, for now we cannot because of the old infrastructure that force loads all translations at once and we must keep them all available
    this._translateService.setTranslation(LANGUAGE.ENGLISH_CANADA, EN_CA, true);
    this._translateService.setTranslation(LANGUAGE.FRENCH_CANADA, FR_CA, true);
    this._translateService.setTranslation(LANGUAGE.SPANISH_MEXICO, ES_MX, true);
    this._translateService.setTranslation(LANGUAGE.SPANISH_COLOMBIA, ES_MX, true);
    this._translateService.setTranslation(LANGUAGE.PORTUGUESE_PORTUGAL, PT_PT, true);

    this._translateService.setDefaultLang(defaultLang);
    this._translateService.use(defaultLang);

    this.translationsReady$ = this.createReadyStream(this._translateService);
  }

  /**
   * Loads the given language stored in session.
   * @returns the language that was loaded
   */
  public loadLanguage(locale: string = LANGUAGE.ENGLISH_CANADA): void {
    if (locale !== this.getCurrentLanguage()) {
      this.switchLanguage(locale);
    }
  }

  public switchLanguage(languageValue: string, callUse: boolean = true): void {
    let language = TranslationService.DEFAULT_LANGUAGE;

    if (Object.values(LANGUAGE).includes(languageValue as LANGUAGE)) {
      language = languageValue as LANGUAGE;
    }

    if (callUse) {
      this._translateService.use(language);
    }
    sessionStorage.setItem(TranslationService.SESSION_LANGUAGE_KEY, JSON.stringify({ LocaleId: language }));
  }

  private createReadyStream(translateService: TranslateService) {

    const langChange$ = this.fromEmitter<LangChangeEvent>(translateService.onLangChange);
    const defaultLangChange$ = this.fromEmitter<DefaultLangChangeEvent>(
      translateService.onDefaultLangChange
    );
    const translationChange$ = this.fromEmitter<TranslationChangeEvent>(
      translateService.onTranslationChange
    );

    const loading$ = this.injector.get(TranslationLoaderHelper).loading$;

    return merge(langChange$, defaultLangChange$, translationChange$).pipe(
      startWith(void 0),
      switchMap(() =>
        loading$.pipe(
          filter(isLoading => !isLoading), // only emit when loading finishes to ensure translations are ready
          take(1)
        )
      ),
      map(() => translateService),
      shareReplay({ bufferSize: 1, refCount: true }) // send the last emmission to new subscribers
    );
  }

  /**
   * Converts EventEmitters into Observables for easy chaining
   */
  public fromEmitter<T>(emitter: EventEmitter<T>) {
    return fromEventPattern<T>(
      handler => emitter.subscribe(handler),
      (_handler, subscription) => subscription.unsubscribe()
    );
  }

  /**
   * Wrapper around TranslateService.currentLang
   * @returns
   */
  public getCurrentLanguage(): LANGUAGE {
    return this._translateService.currentLang as LANGUAGE;
  }

  /**
   * Wrapper around TranslateService.instant
   */
  public instant(key: string, params?: Object): string {
    return this._translateService.instant(key, params);
  }

  /**
   * Wrapper around TranslateService.get
   */
  public get(key: string | string[], params?: Object): Observable<string> {
    return this._translateService.get(key, params);
  }
}
